import { useOnboardingStore } from '../stores/onboardingStore'
import TrustLockup from './TrustLockup'
import ThemeToggle from './ThemeToggle'

/* ============================================================
   ThankYouScreen — the post-submit confirmation, restyled to live in the
   SAME place as the cinematic welcome (ScrollWelcome's final screen):
   theme-aware grey/dark wash, the big blind-debossed spiral on the RIGHT,
   and the content left-anchored in the welcome column on the LEFT.

   It reuses the welcome's final-screen shell verbatim (.sw-welcome-layer
   .is-in, .sw-brandmark, .sw-emboss, .sw-final-body, .sw-welcome, .bottom-bar)
   so the wash + spiral are pixel-identical in both light + dark. The only new
   class is .ty-layer, which pins the reused layer to the full viewport (the
   welcome version is an absolute overlay inside a sticky scroll stage; here it
   is the page itself). The success content keeps its existing .success-v2-*
   styling + behavior — only its home moved into the left column.
   ============================================================ */

export default function ThankYouScreen() {
  const formData = useOnboardingStore((s) => s.formData)
  const submissionId = useOnboardingStore((s) => s.submissionId)
  const firstName = String(
    formData?.owners?.owners?.[0]?.fullName || '',
  )
    .trim()
    .split(/\s+/)[0]
  const practiceName = formData?.details?.practiceName

  return (
    <div className="sw-welcome-layer is-in ty-layer">
      {/* theme toggle — same control as the welcome, top-right */}
      <ThemeToggle className="sw-theme-toggle" />

      {/* brand anchor — full teal icon + BRITE wordmark lockup, top-left */}
      <img
        className="sw-brandmark"
        src="/welcome/brite-lockup-dark.png"
        alt="BRITE"
      />

      {/* RIGHT — brand as material: huge blind-debossed spiral in the grey */}
      <div className="sw-emboss" aria-hidden="true" />

      {/* LEFT — the confirmation, left-anchored in the welcome column */}
      <div className="sw-final-body">
        <div className="sw-welcome ty-welcome">
          <div className="sw-welcome-rule" aria-hidden="true" />

          <div className="success-v2-icon" aria-hidden="true">
            <span className="success-v2-icon-ring success-v2-icon-ring--outer" />
            <span className="success-v2-icon-ring success-v2-icon-ring--mid" />
            <span className="success-v2-icon-ring success-v2-icon-ring--inner">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12.5l4.5 4.5L19 7" />
              </svg>
            </span>
          </div>

          <h1 className="success-v2-headline">
            Thanks {firstName || 'so much'} — our team will reach out shortly.
          </h1>
          <p className="success-v2-subline">
            {practiceName
              ? `We've received your submission for ${practiceName}.`
              : "We've received your submission."}{' '}
            We'll be in touch within 24 hours to schedule onboarding.
          </p>

          <div className="success-v2-note">
            <span className="success-v2-note-quote" aria-hidden="true">"</span>
            <p>
              Our onboarding lead will reach out to walk you through next steps,
              confirm your kit shipment, and get your team set up. Talk soon.
            </p>
          </div>

          <div className="success-v2-actions">
            <a
              href="mailto:support@brite.live"
              className="success-v2-cta-secondary"
            >
              Need help? support@brite.live
            </a>
          </div>

          {submissionId && (
            <div className="success-v2-refid">
              <span className="success-v2-refid-label">Reference</span>
              <code className="success-v2-refid-code">{submissionId}</code>
            </div>
          )}
        </div>
      </div>

      <footer className="bottom-bar">
        <div className="bb-left" aria-hidden="true" />
        <div className="bb-center">
          <TrustLockup />
        </div>
        <div className="bb-right" aria-hidden="true" />
      </footer>
    </div>
  )
}
